import React, {
  ForwardedRef,
  forwardRef,
  memo,
  useCallback,
  useMemo,
} from 'react';
import {
  Box,
  Breakpoint,
  Input,
  Text,
  TextInput,
  useBreakpoint,
} from '@bonanza-social/moonlight';
import { BottomSheet, SelectableOptionItem } from '@bonanza-social/navigation';
import {
  FullAddress,
  useDebouncedAddressSearch,
} from '@bonanza-social/details-attestation';
import { retrieveAddress } from '@bonanza-social/toolkit/api';
import { unwrapResult, useDispatch } from '@bonanza-social/toolkit/state';
import { Platform, Pressable } from 'react-native';
import { RegistrationFieldProps } from '../../RegistrationField.types';
import { useTranslation } from '@bonanza-social/i18n';
import ManualAddressField from '../AddressField/ManualAddressField/ManualAddressField';

function AddressSelectField(
  { value, error, onValue }: RegistrationFieldProps<'address'>,
  ref: ForwardedRef<TextInput>,
) {
  const dispatch = useDispatch();
  const { performDebouncedAddressSearch } = useDebouncedAddressSearch();
  const { t } = useTranslation('registration');
  const breakpoint = useBreakpoint();
  const isDesktop = breakpoint === Breakpoint.DESKTOP;
  const snapPoints = useMemo(
    () => (isDesktop ? ['75%'] : ['100%']),
    [isDesktop],
  );
  const maxHeight = useMemo(() => (isDesktop ? '50%' : '65%'), [isDesktop]);

  const hasErrorValues = useMemo(() => {
    return Boolean(
      error &&
        typeof error === 'object' &&
        Object.values(error).some((value) => value != null && value !== ''),
    );
  }, [error]);

  const handleSuggestionSelect = useCallback(
    async (selectableOptionItem: SelectableOptionItem) => {
      if (!selectableOptionItem?.dynamicValues?.Id) {
        onValue({
          suggestionSelected: false,
          useAutoAddress: true,
          autocompleteAddress: '',
          addressContainer: undefined,
          streetName: '',
          unit: '',
          buildingNumber: '',
          city: '',
          postalCode: '',
          country: 'Canada',
          countryCode: 'CA',
          province: 'Ontario',
          provinceCode: 'ON',
        });
        return;
      }

      const id = selectableOptionItem.dynamicValues.Id;

      try {
        const action = await dispatch(retrieveAddress(id));
        const result = unwrapResult(action);

        const first = result?.Items?.[0];

        if (!first) {
          onValue({
            ...value,
            suggestionSelected: false,
            useAutoAddress: true,
            autocompleteAddress:
              selectableOptionItem?.text ?? value?.autocompleteAddress ?? '',
            addressContainer: id,
          });

          return;
        }

        const {
          CountryName = '',
          CountryIso2 = '',
          Line1 = '',
          PostalCode = '',
          City = '',
          SubBuilding = '',
          BuildingNumber = '',
          Street = '',
          ProvinceCode = '',
          ProvinceName = '',
        } = first as FullAddress;

        onValue({
          suggestionSelected: true,
          useAutoAddress: true,
          autocompleteAddress: `${Line1}, ${City}, ${ProvinceName}, ${PostalCode}`,
          addressContainer: value?.addressContainer,
          country: CountryName,
          countryCode: CountryIso2,
          streetName: Street,
          unit: SubBuilding,
          buildingNumber: BuildingNumber,
          city: City,
          province: ProvinceName,
          provinceCode: ProvinceCode,
          postalCode: PostalCode,
        });
      } catch (e) {
        onValue({
          ...value,
          suggestionSelected: false,
          useAutoAddress: true,
          autocompleteAddress:
            selectableOptionItem?.text ?? value?.autocompleteAddress ?? '',
          addressContainer: id,
        });
      }
    },
    [dispatch, onValue, value],
  );

  const onAddressPressIn = useCallback(() => {
    BottomSheet.open({
      namespace: 'common',
      type: 'AddressSearch',
      detached: Platform.select({ ios: false, web: true, android: false }),
      snapPoints: snapPoints,
      enableDynamicSizing: false,
      contentPaddingHorizontal: 'space-0',
      data: {
        headerNS: 'registration',
        headerKey: 'addressLabel',
        buttonNS: 'registration',
        buttonKey: 'saveButton',
        maxHeight: maxHeight,
        centerContent: true,
        defaultValue: value?.autocompleteAddress,
        value: value?.autocompleteAddress,
        fetchAddresses: performDebouncedAddressSearch,
        onSelect: handleSuggestionSelect,
      },
    });
  }, [
    value?.autocompleteAddress,
    handleSuggestionSelect,
    performDebouncedAddressSearch,
    maxHeight,
    snapPoints,
  ]);

  const handleToggle = useCallback(() => {
    onValue({
      ...value,
      useAutoAddress: !value.useAutoAddress,
    });
  }, [value, onValue]);

  if (value.useAutoAddress) {
    return (
      <Box>
        <Box marginBottom="space-400">
          <Text
            color="darkSurfacePrimary"
            marginBottom="space-300"
            variant="h5"
            locNS="registration"
            locKey="addressLabel"
          />
          <Pressable onPress={onAddressPressIn}>
            <Input
              ref={ref}
              value={value?.autocompleteAddress}
              placeholderNS="registration"
              placeholderKey="searchAddress"
              error={error}
              editable={false}
              onPressIn={onAddressPressIn}
            />
          </Pressable>
          {hasErrorValues && (
            <Text
              color="darkSurfaceError"
              variant="body-m"
              marginTop="space-200"
            >
              {t('genericAddressError')}
            </Text>
          )}
          <Pressable onPress={handleToggle}>
            <Box
              flexDirection="column"
              marginTop="space-200"
              alignItems="flex-end"
            >
              <Text
                variant="link-medium"
                color="darkSurfacePrimary"
                locNS="registration"
                locKey="enterManually"
              />
            </Box>
          </Pressable>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <ManualAddressField value={value} error={error} onValue={onValue} />
      <Pressable onPress={handleToggle}>
        <Box flexDirection="column" alignItems="flex-end">
          <Text
            variant="link-medium"
            color="darkSurfacePrimary"
            locNS="registration"
            locKey="switchToAuto"
          />
        </Box>
      </Pressable>
    </Box>
  );
}
export default memo(forwardRef(AddressSelectField));
